// src/pages/media/PistonSection3.jsx
import React, { useRef, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import pistonImage from '../../assets/images/singleturbine5.jpg'
import PistonSlider from './SingleTurbineSlider'

gsap.registerPlugin(ScrollTrigger)

export default function SingleTurbineSection3() {
  const sectionRef     = useRef(null)
  const pinRef         = useRef(null)
  const overlayRef     = useRef(null)
  const scrollBlockRef = useRef(null)

  useLayoutEffect(() => {
    const isMobile = window.innerWidth < 768
    const ctx = gsap.context(() => {
      if (!isMobile) {
        // ───────── DESKTOP (unchanged) ─────────
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger:       sectionRef.current,
            start:         'top top',
            end:           'bottom top',
            scrub:         true,
            pin:           pinRef.current,
            pinSpacing:    false,
            anticipatePin: 0.5,
          }
        })
        tl.to(overlayRef.current, { opacity: 0.9, duration: 1, ease: 'none' }, 0)
          .fromTo(scrollBlockRef.current,
            { y: 100, opacity: 0 },
            { y: 0,   opacity: 1, duration: 0.5, ease: 'none' },
            0.3
          )
          .to(scrollBlockRef.current,
            { y: -window.innerHeight * 1.3, duration: 1.3, ease: 'none' },
            1
          )
      } else {
        // ───────── MOBILE ─────────
        const mobileTL = gsap.timeline({
          scrollTrigger: {
            trigger:       sectionRef.current,
            start:         'top top',
            end:           '+=240%',       // 260vh total
            scrub:         true,
            pin:           pinRef.current,
            pinSpacing:    false,
            anticipatePin: 0.5,
          }
        })
        mobileTL
          .to(overlayRef.current, { opacity: 0.9, duration: 0.2, ease: 'none' }, 0)
          .fromTo(scrollBlockRef.current,
            { y: 80, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.3, ease: 'none' },
            0
          )
          .to({}, { duration: 0.2 })  // hold
          .to(scrollBlockRef.current,
            { y: -window.innerHeight * 0.8, duration: 0.5, ease: 'none' },
            '+=0'
          )
      }
    }, sectionRef)

    return () => {
      ctx.revert()
      ScrollTrigger.getAll().forEach(t => t.kill())
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      className="
        relative w-full overflow-hidden bg-black
        h-[260vh]      /* mobile */
        md:h-[380vh]   /* desktop */
      "
    >
      <div
        ref={pinRef}
        className="sticky top-0 h-screen w-full overflow-hidden"
      >
        {/* background */}
        <img
          src={pistonImage}
          alt="Exteriors that Shine"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* dark gradient */}
        <div
          ref={overlayRef}
          className="absolute inset-0 bg-gradient-to-b from-black/40 to-black opacity-0 pointer-events-none z-10"
        />

        {/* content + slider */}
        <div
          ref={scrollBlockRef}
          className="
            relative z-20 w-full 
            px-6 md:px-8 
            text-white 
            space-y-5 md:space-y-24
          "
        >
          {/* copy */}
          <div className="flex flex-col justify-center items-start min-h-[60vh]">
            <h2 className="text-5xl md:text-6xl font-bold mb-4">
              Exteriors that Shine
            </h2>
            <p className="text-xl md:text-2xl max-w-lg leading-relaxed">
              Our aircraft exteriors are built with precision, comfort, and sleek finishes—perfectly suited for elevated missions.
            </p>
          </div>

          {/* slider */}
          <div className="h-[60vh] md:min-h-screen flex items-center justify-center w-full">
            <div className="w-full bg-black">
              <PistonSlider />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
